import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  mutationEditorReset,
  mutationEditorChapterSelect,
  mutationEditorChapterUpdate,
  mutationEditorChapterMove,
} from 'gql/mutations/editor';
import { useMutationChapterDuplicate } from 'gql/mutations/chapter';
import { useQueryLocalEditor } from 'gql/queries/local';
import { notify } from 'uikit/Notifications';
import { trackEvent, reportError } from 'lib/tracking';

import ChapterCard from './ChapterCard';

const ChapterCardContainer = ({ chapter, ...props }) => {
  const { t } = useTranslation();
  const {
    data: {
      editor: { status: editorStatus, chapterSelected, questionEditing },
    },
  } = useQueryLocalEditor();
  const [isExpandedLocal, setExpanded] = useState(false);
  const isSelected = chapterSelected?.id === chapter.id;
  const isExpanded =
    isExpandedLocal ||
    (editorStatus === 'question-create' &&
      chapterSelected?.id === chapter.id) ||
    (editorStatus === 'question-update' &&
      questionEditing?.chapterId === chapter.id);
  const handleChapterExpandedToggle = useCallback((evt) => {
    evt.stopPropagation();
    setExpanded((state) => !state);
  }, []);
  const handleChapterSelect = useCallback(() => {
    mutationEditorChapterSelect(chapter);
    setExpanded((state) => !state);
  }, [chapter]);
  const handleChapterUpdate = useCallback(() => {
    mutationEditorChapterUpdate(chapter);
  }, [chapter]);
  const handleChapterMove = useCallback(() => {
    mutationEditorChapterMove(chapter);
  }, [chapter]);
  const mutationChapterDuplicate = useMutationChapterDuplicate();
  const handleChapterDuplicate = useCallback(async () => {
    try {
      mutationEditorReset();
      await mutationChapterDuplicate(chapter);
      trackEvent('chapter', 'duplicate');
    } catch (err) {
      notify(
        'error',
        t('Course/Teacher/Editor/Finder/ChapterCard/chapter-duplicate-error'),
      );
      reportError('chapter', 'duplicate', err);
    }
  }, [chapter, mutationChapterDuplicate, t]);
  return (
    <ChapterCard
      chapter={chapter}
      handleChapterDuplicate={handleChapterDuplicate}
      handleChapterExpandedToggle={handleChapterExpandedToggle}
      handleChapterMove={handleChapterMove}
      handleChapterSelect={handleChapterSelect}
      handleChapterUpdate={handleChapterUpdate}
      isExpanded={isExpanded}
      isSelected={isSelected}
      t={t}
      {...props}
    />
  );
};

ChapterCardContainer.propTypes = {
  chapter: PropTypes.object.isRequired,
};

export default ChapterCardContainer;
